import React, { useEffect, useState } from "react";
import $ from "jquery";
import { Image as ImageIcon, Save, Upload } from "lucide-react";
import ApiURl from "../../controllers/Api";

const DEFAULT_FORM = {
  eyebrow_top: "Signature",
  title_main: "Gold",
  eyebrow_bottom: "Timeless",
  title_accent: "Stories.",
  lead_text: "Jewellery crafted for celebrations, heirlooms and everyday grace.",
  body_text:
    "Discover refined gold collections shaped with careful detailing, warm finishes and a timeless Indian sensibility. Every piece is created to become part of your most meaningful moments.",
  primary_button_text: "Explore Collection",
  primary_button_url: "/all-products",
  secondary_button_text: "Our Story",
  secondary_button_url: "/about-us",
};

export default function AdminHomeHero() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [files, setFiles] = useState({
    image_one: null,
    image_two: null,
    image_three: null,
  });
  const [previews, setPreviews] = useState({
    image_one: "",
    image_two: "",
    image_three: "",
  });
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await $.getJSON(`${ApiURl}getHomeHero.php`);
        if (!response?.success || !response?.data) return;

        const data = response.data;

        setForm((prev) => ({
          ...prev,
          eyebrow_top: data.eyebrow_top || prev.eyebrow_top,
          title_main: data.title_main || prev.title_main,
          eyebrow_bottom: data.eyebrow_bottom || prev.eyebrow_bottom,
          title_accent: data.title_accent || prev.title_accent,
          lead_text: data.lead_text || prev.lead_text,
          body_text: data.body_text || prev.body_text,
          primary_button_text:
            data.primary_button_text || prev.primary_button_text,
          primary_button_url:
            data.primary_button_url || prev.primary_button_url,
          secondary_button_text:
            data.secondary_button_text || prev.secondary_button_text,
          secondary_button_url:
            data.secondary_button_url || prev.secondary_button_url,
        }));

        setPreviews({
          image_one: data.image_one_url || "",
          image_two: data.image_two_url || "",
          image_three: data.image_three_url || "",
        });
      } catch (error) {
        console.error("Error loading Home Hero settings:", error);
        setMessage("Could not load saved Home Hero settings.");
      }
    };

    fetchSettings();
  }, []);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (field) => (event) => {
    const file = event.target.files?.[0] || null;

    setFiles((prev) => ({
      ...prev,
      [field]: file,
    }));

    setPreviews((prev) => ({
      ...prev,
      [field]: file ? URL.createObjectURL(file) : prev[field],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");

    const payload = new FormData();

    Object.entries(form).forEach(([key, value]) => {
      payload.append(key, value);
    });

    Object.entries(files).forEach(([key, file]) => {
      if (file) payload.append(key, file);
    });

    try {
      const response = await $.ajax({
        url: `${ApiURl}updateHomeHero.php`,
        type: "POST",
        data: payload,
        processData: false,
        contentType: false,
        dataType: "json",
      });

      if (response?.success) {
        setMessage("Home Hero updated successfully.");
        setFiles({
          image_one: null,
          image_two: null,
          image_three: null,
        });
      } else {
        setMessage(response?.message || "Failed to update Home Hero.");
      }
    } catch (error) {
      console.error("Error saving Home Hero settings:", error);
      setMessage("Server error while saving Home Hero.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-home-hero-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      <div className="mx-auto max-w-[1450px]">
        <div className="overflow-hidden rounded-[26px] border border-[#d8c6b7] bg-[#fffaf5] shadow-[0_22px_60px_rgba(43,0,0,0.08)]">
          <div className="border-b border-[#e3d6cb] bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.14),transparent_34%),linear-gradient(135deg,#fffaf5,#f2e6dc)] px-5 py-6 sm:px-7 sm:py-8">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#9a7740]">
              Homepage Configuration
            </p>

            <h1 className="font-serif text-3xl font-medium tracking-tight text-[#2B0000] sm:text-4xl">
              Home Hero Section
            </h1>

            <p className="mt-3 max-w-3xl text-sm leading-6 text-[#6f5e57]">
              Edit the homepage hero text, button labels, destination links,
              and all three showcase images from one place.
            </p>
          </div>

          <div className="p-4 sm:p-6 lg:p-7">
            {message ? (
              <div className="mb-6 rounded-2xl border border-[#d4af37]/25 bg-[#fff7e6] px-4 py-3 text-sm font-semibold text-[#7d5a19] shadow-[0_10px_25px_rgba(43,0,0,0.05)]">
                {message}
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-7">
              <section className="rounded-[22px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f3e8dd)] p-4 shadow-[0_16px_42px_rgba(43,0,0,0.06)] sm:p-6">
                <div className="mb-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#9a7740]">
                    Hero Copy
                  </p>

                  <h2 className="mt-1 font-serif text-2xl font-medium text-[#2B0000]">
                    Headings and buttons
                  </h2>

                  <p className="mt-1 text-sm text-[#75645d]">
                    Keep the text short, premium, and easy to read.
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <TextInput
                    label="Top Small Text"
                    value={form.eyebrow_top}
                    onChange={(value) =>
                      updateForm("eyebrow_top", value)
                    }
                  />

                  <TextInput
                    label="Main Title"
                    value={form.title_main}
                    onChange={(value) =>
                      updateForm("title_main", value)
                    }
                  />

                  <TextInput
                    label="Second Small Text"
                    value={form.eyebrow_bottom}
                    onChange={(value) =>
                      updateForm("eyebrow_bottom", value)
                    }
                  />

                  <TextInput
                    label="Second Title"
                    value={form.title_accent}
                    onChange={(value) =>
                      updateForm("title_accent", value)
                    }
                  />

                  <TextInput
                    label="Primary Button Text"
                    value={form.primary_button_text}
                    onChange={(value) =>
                      updateForm("primary_button_text", value)
                    }
                  />

                  <TextInput
                    label="Primary Button Link"
                    value={form.primary_button_url}
                    onChange={(value) =>
                      updateForm("primary_button_url", value)
                    }
                  />

                  <TextInput
                    label="Secondary Button Text"
                    value={form.secondary_button_text}
                    onChange={(value) =>
                      updateForm("secondary_button_text", value)
                    }
                  />

                  <TextInput
                    label="Secondary Button Link"
                    value={form.secondary_button_url}
                    onChange={(value) =>
                      updateForm("secondary_button_url", value)
                    }
                  />
                </div>

                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  <TextArea
                    label="Lead Text"
                    value={form.lead_text}
                    onChange={(value) =>
                      updateForm("lead_text", value)
                    }
                  />

                  <TextArea
                    label="Description"
                    value={form.body_text}
                    onChange={(value) =>
                      updateForm("body_text", value)
                    }
                  />
                </div>
              </section>

              <section className="rounded-[22px] border border-[#decfc3] bg-white p-4 shadow-[0_16px_42px_rgba(43,0,0,0.06)] sm:p-6">
                <div className="mb-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#9a7740]">
                    Showcase Media
                  </p>

                  <h2 className="mt-1 font-serif text-2xl font-medium text-[#2B0000]">
                    Hero images
                  </h2>

                  <p className="mt-1 text-sm text-[#75645d]">
                    Upload clean, high-resolution jewellery images for each
                    hero position.
                  </p>
                </div>

                <div className="grid gap-5 lg:grid-cols-3">
                  <ImageField
                    label="Top Circle Image"
                    preview={previews.image_one}
                    onChange={handleImageChange("image_one")}
                  />

                  <ImageField
                    label="Bottom Pill Image"
                    preview={previews.image_two}
                    onChange={handleImageChange("image_two")}
                  />

                  <ImageField
                    label="Right Arch Image"
                    preview={previews.image_three}
                    onChange={handleImageChange("image_three")}
                  />
                </div>
              </section>

              <div className="flex flex-col gap-3 rounded-[20px] border border-[#e3d6cb] bg-[#fbf3eb] p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                <div>
                  <p className="text-sm font-semibold text-[#4a352f]">
                    Ready to publish your hero changes?
                  </p>

                  <p className="mt-1 text-xs text-[#7d6c64]">
                    Existing images remain unchanged unless a new file is
                    selected.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2B0000] px-5 py-3 text-sm font-semibold text-[#f4dfae] shadow-[0_12px_28px_rgba(43,0,0,0.16)] transition hover:-translate-y-0.5 hover:bg-[#4A090B] disabled:cursor-not-allowed disabled:bg-[#b7a69b] disabled:text-white"
                >
                  <Save className="h-4 w-4" />
                  {saving ? "Saving..." : "Save Home Hero"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .admin-home-hero-luxury {
          background:
            radial-gradient(circle at 7% 0%, rgba(212, 175, 55, 0.1), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43, 0, 0, 0.04), transparent 30%),
            #F7EFE7;
        }

        .admin-home-hero-luxury input,
        .admin-home-hero-luxury textarea,
        .admin-home-hero-luxury button {
          font: inherit;
        }

        .admin-home-hero-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .admin-home-hero-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143, 111, 56, 0.5)
            rgba(212, 175, 55, 0.07);
        }

        .admin-home-hero-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .admin-home-hero-luxury *::-webkit-scrollbar-track {
          background: rgba(212, 175, 55, 0.07);
        }

        .admin-home-hero-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143, 111, 56, 0.5);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .admin-home-hero-luxury {
            padding: 10px;
          }
        }
      `}</style>
    </div>
  );
}

function TextInput({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-[#4a352f]">
        {label}
      </span>

      <input
        type="text"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
      />
    </label>
  );
}

function TextArea({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-[#4a352f]">
        {label}
      </span>

      <textarea
        rows={5}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className="w-full resize-y rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm leading-6 text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
      />
    </label>
  );
}

function ImageField({ label, preview, onChange }) {
  return (
    <div className="rounded-[20px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f3e8dd)] p-4 shadow-[0_12px_32px_rgba(43,0,0,0.05)]">
      <div className="mb-3 flex items-center gap-2">
        <div className="rounded-lg border border-[#d4af37]/25 bg-white p-2 shadow-sm">
          <ImageIcon className="h-4 w-4 text-[#8f6f38]" />
        </div>

        <div>
          <p className="text-sm font-semibold text-[#2B0000]">{label}</p>
          <p className="mt-0.5 text-xs text-[#7d6c64]">
            JPG, PNG or WEBP
          </p>
        </div>
      </div>

      <div className="mb-4 h-52 overflow-hidden rounded-[18px] border border-[#d8c6b7] bg-[#fbf3eb] shadow-inner">
        {preview ? (
          <img
            src={preview}
            alt={label}
            className="h-full w-full bg-[#fffaf5] object-contain"
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-[#9b877e]">
            <ImageIcon className="h-8 w-8" />
            <span className="text-sm">No image selected</span>
          </div>
        )}
      </div>

      <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm font-semibold text-[#2B0000] shadow-sm transition hover:border-[#c59a4a] hover:bg-[#fff7ed]">
        <Upload className="h-4 w-4" />
        Upload Image

        <input
          type="file"
          accept="image/*"
          onChange={onChange}
          className="hidden"
        />
      </label>
    </div>
  );
}